import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { formatFull } from "@/lib/format";
import { StatusBadge, ConfBadge } from "@/components/Badges";
import OpportunityForm from "@/components/OpportunityForm";
import { ALL_COUNTRIES, PROPERTY_TYPES, OPP_STATUS, CONFIDENTIALITY, CONFIDENTIALITY_LABELS } from "@/lib/constants";
import { toast } from "sonner";
import { MapPin, Maximize, TrendingUp, Plus, Pencil, Trash2, ImageOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { motion } from "framer-motion";

function FilterSelect({ value, onChange, options, placeholder, testid, labels }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-10 w-full sm:w-40" data-testid={testid}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all" data-testid={`${testid}-all`}>{placeholder}</SelectItem>
        {options.map((o) => (
          <SelectItem key={o} value={o} data-testid={`${testid}-${o}`}>{labels ? labels[o] || o : o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function Opportunities() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("all");
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const [conf, setConf] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get("/opportunities").then((r) => setItems(r.data));
  useEffect(() => {
    load();
  }, []);

  const filtered = items.filter(
    (o) =>
      (country === "all" || o.country === country) &&
      (type === "all" || o.property_type === type) &&
      (status === "all" || o.status === status) &&
      (conf === "all" || o.confidentiality === conf) &&
      (o.title.toLowerCase().includes(q.toLowerCase()) ||
        o.city.toLowerCase().includes(q.toLowerCase()) ||
        o.country.toLowerCase().includes(q.toLowerCase()))
  );

  const openAdd = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (e, o) => {
    e.stopPropagation();
    setEditing(o);
    setFormOpen(true);
  };
  const confirmDelete = async () => {
    try {
      await api.delete(`/opportunities/${deleting.id}`);
      toast.success(t("opportunities.toast.deleted"));
      setDeleting(null);
      load();
    } catch {
      toast.error(t("opportunities.toast.deleteError"));
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-10 lg:px-12" data-testid="opportunities-page">
      <header className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="overline text-[#C6A87C]">{t("opportunities.overline")}</p>
          <h1 className="mt-2 font-display text-4xl tracking-tight text-[#0F172A] sm:text-5xl">
            {t("opportunities.title")}
          </h1>
          <p className="mt-2 text-slate-500">{t("opportunities.subtitle", { count: items.length })}</p>
        </div>
        <Button onClick={openAdd} data-testid="add-opportunity-btn" className="rounded-full bg-[#0F172A] text-white hover:bg-[#1e293b]">
          <Plus className="mr-2 h-4 w-4" /> {t("opportunities.add")}
        </Button>
      </header>

      <div className="mb-8 flex flex-col gap-3 lg:flex-row lg:items-center">
        <Input
          data-testid="opportunities-search"
          placeholder={t("opportunities.searchPlaceholder")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="h-10 lg:w-72"
        />
        <div className="flex flex-wrap gap-3">
          <FilterSelect value={country} onChange={setCountry} options={ALL_COUNTRIES} placeholder={t("common.allCountries")} testid="filter-country" />
          <FilterSelect value={type} onChange={setType} options={PROPERTY_TYPES} placeholder={t("common.allTypes")} testid="filter-type" />
          <FilterSelect value={status} onChange={setStatus} options={OPP_STATUS} placeholder={t("common.allStatuses")} testid="filter-status" />
          <FilterSelect value={conf} onChange={setConf} options={CONFIDENTIALITY} placeholder={t("opportunities.confidentiality")} testid="filter-conf" labels={CONFIDENTIALITY_LABELS} />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
        {filtered.map((o, i) => (
          <motion.article
            key={o.id}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.04, duration: 0.4 }}
            data-testid={`opportunity-card-${o.id}`}
            onClick={() => navigate(`/opportunites/${o.id}`)}
            className="group relative cursor-pointer overflow-hidden rounded-2xl border border-slate-200 bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
          >
            <div className="relative h-52 overflow-hidden bg-slate-100">
              {o.photos?.[0] ? (
                <img src={fileUrl(o.photos[0])} alt={o.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              ) : (
                <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-slate-300" data-testid={`opportunity-nophoto-${o.id}`}>
                  <ImageOff className="h-8 w-8" />
                  <span className="text-xs">{t("opportunities.noPhoto")}</span>
                </div>
              )}
              <div className="absolute left-3 top-3">
                <ConfBadge level={o.confidentiality} />
              </div>
              <div className="absolute right-3 top-3">
                <StatusBadge status={o.status} />
              </div>
              <div className="absolute bottom-3 right-3 flex gap-2 opacity-0 transition-opacity group-hover:opacity-100">
                <button
                  onClick={(e) => openEdit(e, o)}
                  data-testid={`edit-opportunity-${o.id}`}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-slate-700 shadow-sm backdrop-blur transition-colors hover:text-[#0F172A]"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setDeleting(o); }}
                  data-testid={`delete-opportunity-${o.id}`}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-red-600 shadow-sm backdrop-blur transition-colors hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <p className="text-xs font-medium text-[#C6A87C]">{o.property_type}</p>
              <h3 className="mt-1 font-display text-2xl leading-tight tracking-tight text-[#0F172A]">{o.title}</h3>
              <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-500">
                <MapPin className="h-3.5 w-3.5" /> {o.city}, {o.country}
              </p>
              <div className="mt-5 flex items-end justify-between border-t border-slate-100 pt-4">
                <div>
                  <p className="overline text-slate-400">{t("opportunities.askingPrice")}</p>
                  <p className="font-display text-2xl text-[#0F172A]">{formatFull(o.asking_price, o.currency)}</p>
                </div>
                <div className="flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-sm font-semibold text-emerald-700">
                  {o.margin == null ? (
                    <span className="text-xs font-medium text-slate-500">{t("opportunities.marginUnavailable")}</span>
                  ) : (
                    <>
                      <TrendingUp className="h-3.5 w-3.5" /> +{formatFull(o.margin, o.currency)}
                    </>
                  )}
                </div>
              </div>
              <div className="mt-3 flex items-center gap-4 text-xs text-slate-400">
                <span className="flex items-center gap-1"><Maximize className="h-3.5 w-3.5" /> {o.surface} m²</span>
                <span>{o.margin_pct == null ? t("opportunities.enterResale") : t("opportunities.margin", { pct: o.margin_pct })}</span>
              </div>
            </div>
          </motion.article>
        ))}
      </div>
      {filtered.length === 0 && (
        <p className="py-20 text-center text-slate-400">{t("opportunities.noResults")}</p>
      )}

      <OpportunityForm open={formOpen} onOpenChange={setFormOpen} initial={editing} onSaved={load} />

      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent data-testid="delete-opportunity-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">{t("opportunities.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("opportunities.deleteDesc", { title: deleting?.title })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" data-testid="delete-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="rounded-full bg-red-600 hover:bg-red-700" data-testid="delete-confirm">
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
